import type { KeyValueStore } from './key-value-store.js';

/** Where the persisted install id is stored. */
const INSTALL_ID_KEY = 'elara_install_id';

/**
 * A stable id for this app install, used to key per-device chat presence on the server.
 *
 * Prefers the Firebase Installation ID when the platform can read one (web: `firebase/installations`;
 * it doesn't rotate like the FCM token). Without one, or when reading it fails (blocked IndexedDB),
 * uses a random id persisted in the key/value store, so the device still keeps one presence record
 * instead of a new one per launch. React Native uses the persisted id: the Firebase Installations
 * native module would need a new store build.
 *
 * @example
 * const getInstallId = createInstallIdProvider(keyValueStore, () => getId(getInstallations(getApp())));
 * await getInstallId(); // 'dOz3...' (the FID), or a persisted random id
 */
export function createInstallIdProvider(
  keyValueStore: KeyValueStore,
  readFirebaseInstallationId?: () => Promise<string>,
): () => Promise<string> {
  const persistedId = (): string => {
    const stored = keyValueStore.get(INSTALL_ID_KEY);
    if (stored) return stored;
    const created = crypto.randomUUID();
    keyValueStore.set(INSTALL_ID_KEY, created);
    return created;
  };

  return async () => {
    if (!readFirebaseInstallationId) return persistedId();
    try {
      return await readFirebaseInstallationId();
    } catch (error) {
      console.debug('[install-id] Firebase Installation ID unavailable; using a persisted id', error);
      return persistedId();
    }
  };
}
